import React, {useState, useEffect} from 'react';
import styled, {keyframes} from 'styled-components';
import {Button, Typography} from 'antd';
import {ArrowRightOutlined, PlayCircleOutlined} from '@ant-design/icons';

const {Title, Paragraph} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Styled Components
const HeroContainer = styled.section`
  position: relative;
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 0;
  padding: 0;
  background: ${props =>
    props.theme.gradients?.dark || 'linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%)'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: ${props =>
      props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
    background-size: 400% 400%;
    animation: ${gradientShift} 15s ease infinite;
    opacity: 0.1;
    z-index: 1;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing?.[20] || '5rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'};
  text-align: center;
`;

const GlassCard = styled.div`
  background: ${props => props.theme.colors?.glass?.light || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(20px);
  border-radius: ${props => props.theme.borderRadius?.['3xl'] || '1.5rem'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: ${props => props.theme.spacing?.[12] || '3rem'}
    ${props => props.theme.spacing?.[8] || '2rem'};
  box-shadow: ${props => props.theme.boxShadow?.glass || '0 8px 32px 0 rgba(31, 38, 135, 0.37)'};
  animation: ${fadeInUp} 1s ease-out;
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props =>
      props.theme.boxShadow?.glassHover || '0 8px 32px 0 rgba(31, 38, 135, 0.5)'};
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[8] || '2rem'}
      ${props => props.theme.spacing?.[6] || '1.5rem'};
  }
`;

const HeroTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.['6xl'] || '3.75rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.black || '900'};
    line-height: ${props => props.theme.typography?.lineHeight?.tight || '1.25'};
    margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
    background: ${props =>
      props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${fadeInUp} 1s ease-out 0.2s both;

    @media (max-width: ${props => props.theme.breakpoints?.lg || '1024px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['5xl'] || '3rem'};
    }

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['4xl'] || '2.25rem'};
    }

    @media (max-width: ${props => props.theme.breakpoints?.sm || '640px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['3xl'] || '1.875rem'};
    }
  }
`;

const HeroSubtitle = styled(Paragraph)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.body || "'Open Sans', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.xl || '1.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    color: ${props => props.theme.colors?.neutral?.[200] || '#e5e5e5'};
    margin-bottom: ${props => props.theme.spacing?.[8] || '2rem'};
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    animation: ${fadeInUp} 1s ease-out 0.4s both;

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing?.[4] || '1rem'};
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  animation: ${fadeInUp} 1s ease-out 0.6s both;

  @media (max-width: ${props => props.theme.breakpoints?.sm || '640px'}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing?.[3] || '0.75rem'};
  }
`;

const PrimaryButton = styled(Button)`
  && {
    background: ${props =>
      props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
    border: none;
    border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
    padding: ${props => props.theme.spacing?.[4] || '1rem'}
      ${props => props.theme.spacing?.[8] || '2rem'};
    height: auto;
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    color: white;
    transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s;
    }

    &:hover,
    &:focus {
      transform: translateY(-2px);
      box-shadow: ${props =>
        props.theme.boxShadow?.xl ||
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
      color: white;

      &::before {
        left: 100%;
      }
    }

    &:active {
      transform: translateY(0);
    }

    &:focus {
      box-shadow: ${props =>
          props.theme.boxShadow?.xl ||
          '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'},
        0 0 0 3px rgba(249, 115, 22, 0.3);
    }

    .anticon {
      margin-left: ${props => props.theme.spacing?.[2] || '0.5rem'};
      position: relative;
      z-index: 1;
    }
  }
`;

const SecondaryButton = styled(Button)`
  && {
    background: transparent;
    border: 2px solid ${props => props.theme.colors?.glass?.medium || 'rgba(255, 255, 255, 0.15)'};
    border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
    padding: ${props => props.theme.spacing?.[4] || '1rem'}
      ${props => props.theme.spacing?.[8] || '2rem'};
    height: auto;
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    color: ${props => props.theme.colors?.neutral?.[100] || '#f5f5f5'};
    backdrop-filter: blur(10px);
    transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};

    &:hover,
    &:focus {
      background: ${props => props.theme.colors?.glass?.medium || 'rgba(255, 255, 255, 0.15)'};
      transform: translateY(-2px);
      color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    }

    .anticon {
      margin-right: ${props => props.theme.spacing?.[2] || '0.5rem'};
    }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;

const FloatingCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: ${props =>
    props.theme.gradients?.accent || 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'};
  opacity: 0.1;
  animation: ${float} ${props => props.duration || '6s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

const ModernHero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleJoinWaitlist = () => {
    // Scroll to waitlist section
    const waitlistSection = document.getElementById('waitlist-section');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({behavior: 'smooth'});
    }
  };

  const handleWatchDemo = () => {
    // Scroll to video section
    const videoSection = document.getElementById('video-section');
    if (videoSection) {
      videoSection.scrollIntoView({behavior: 'smooth'});
    }
  };

  return (
    <HeroContainer>
      <FloatingElements>
        <FloatingCircle
          style={{
            width: '100px',
            height: '100px',
            top: '20%',
            left: '10%',
          }}
          duration="8s"
          delay="0s"
        />
        <FloatingCircle
          style={{
            width: '60px',
            height: '60px',
            top: '60%',
            right: '15%',
          }}
          duration="10s"
          delay="2s"
        />
        <FloatingCircle
          style={{
            width: '80px',
            height: '80px',
            bottom: '20%',
            left: '20%',
          }}
          duration="12s"
          delay="4s"
        />
      </FloatingElements>

      <ContentWrapper>
        <GlassCard>
          <HeroTitle level={1}>Empowering the Next Generation of Creatives</HeroTitle>

          <HeroSubtitle>
            Connect with brands, showcase your talent, and build your creative career. CLLCTVE
            bridges the gap between college creatives and forward-thinking brands targeting Gen-Z
            consumers.
          </HeroSubtitle>

          <ButtonGroup>
            <PrimaryButton size="large" onClick={handleJoinWaitlist} icon={<ArrowRightOutlined />}>
              Join the Waitlist
            </PrimaryButton>

            <SecondaryButton size="large" onClick={handleWatchDemo} icon={<PlayCircleOutlined />}>
              Watch Demo
            </SecondaryButton>
          </ButtonGroup>
        </GlassCard>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default ModernHero;
